import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SurveyServiceService } from 'library/services/survey-service.service';
import { ToastrService } from 'ngx-toastr';

export interface Language {
  id: number;
  name: string;
  code: string;
  saved?: boolean;
}

interface SurveyData {
  surveyId: number;
  languageId: number;
  questions: any[];
}

interface TableField {
  label: string;
  type: 'Numbers' | 'Text Field' | 'Attachment';
  id: number;
}

interface TemplateItem {
  id: number;
  name: string;
  icon?: string;
}

interface DisplayShareField {
  label: string;
  id: number;
  order: number;
}

@Component({
  selector: 'lib-dynamic-form',
  standalone: true,
  imports: [
    CommonModule,
    MatGridListModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatIconModule,
    MatListModule,
    MatCheckboxModule,
    MatRadioModule,
    MatTooltipModule,
    DragDropModule,
    MatChipsModule,
    TranslateModule,
    MatDatepickerModule,
    MatNativeDateModule
  ],
  templateUrl: './dynamic-form.component.html',
  styleUrl: './dynamic-form.component.scss'
})
export class DynamicFormComponent implements OnInit, OnDestroy {
  @Input() public initialSurveyData: any[] = [];
  @Input() public readOnly: boolean = false;
  @Input() public showConditions: boolean = true;
  @Input() public allowedQuestionTypes?: string[];
  @Input() public containerHeight: string = 'calc(100vh - 250px)';
  @Input() public availableLanguages: Language[] = [
    { id: 2, name: 'English', code: 'en' }
  ];
  @Input() public surveyId: number = 0;

  @Output() public surveyDataChange = new EventEmitter<Partial<SurveyData>>();
  @Output() public questionSelected = new EventEmitter<number>();
  @Output() public questionAdded = new EventEmitter<any>();
  @Output() public questionDeleted = new EventEmitter<number>();
  @Output() public formSaved = new EventEmitter<SurveyData>();
  @Output() public languageRemoved = new EventEmitter<number>();
  @Output() public publishClicked = new EventEmitter<void>();

  public questions: FormGroup[] = [];
  public surveyData: Partial<SurveyData> = {
    languageId: 2,
    questions: []
  };
  public gridRowHeight = window.innerHeight - 100;
  public selectedQuestionType = 'radio';
  public selectedQuestionIndex: number | null = null;

  public questionTypes = [
    { value: 'radio', label: 'Radio Button', icon: 'radio_button_checked', code: 4 },
    { value: 'multipleChoice', label: 'Multiple Choice', icon: 'check_box', code: 9 },
    { value: 'text', label: 'Text Field', icon: 'text_fields', code: 1 },
    { value: 'yesNo', label: 'Yes/No', icon: 'toggle_on', code: 8 },
    { value: 'fileUpload', label: 'Upload Image', icon: 'upload_file', code: 2 },
    { value: 'salesStock', label: 'Sales/Stock Template', icon: 'inventory', code: 6 },
    { value: 'date', code: 12, label: 'Date', icon: 'calendar_today' },
    { value: 'existingTemplate', label: 'Existing Template', icon: 'dashboard', code: 7 },
  ];
  public defaultQuestionType = this.questionTypes[0].value;

  private questionEditingStates: { [key: string]: boolean } = {};

  public languageControl: FormControl<number>;
  public selectedLanguage: number = 2;
  private languageData: { [key: number]: any } = {}; // Store data for each language

  public isTemplateMenuOpen = false;
  public selectedTemplateItem: number | null = null;
  public templateItems: TemplateItem[] = [
    { id: 7, name: 'Sellout' },
    { id: 13, name: 'Store360' },
    { id: 14, name: 'Display Planogram' },
    { id: 16, name: 'Display Share' },
    { id: 17, name: 'POSM Deployment' },
    { id: 18, name: 'Promoter Share' },
    { id: 20, name: 'Shop Profiling' },
    { id: 21, name: 'Market Activities' }
  ];

  public selloutTable: TableField[] = [
    { label: 'DYNAMIC_FORM.SELLOUT.MODEL', type: 'Numbers', id: 1 },
    { label: 'DYNAMIC_FORM.SELLOUT.QTY', type: 'Numbers', id: 7 },
    { label: 'DYNAMIC_FORM.SELLOUT.PRICE', type: 'Numbers', id: 5 },
    { label: 'DYNAMIC_FORM.SELLOUT.CUSTOMER_NO', type: 'Numbers', id: 5 },
    { label: 'DYNAMIC_FORM.SELLOUT.CUSTOMER_NAME', type: 'Text Field', id: 1 },
    { label: 'DYNAMIC_FORM.SELLOUT.CUSTOMER_EMAIL', type: 'Text Field', id: 1 },
    { label: 'DYNAMIC_FORM.SELLOUT.SALES_DATE', type: 'Text Field', id: 14 },
    { label: 'DYNAMIC_FORM.SELLOUT.INVOICE', type: 'Attachment', id: 12 }
  ];

  public savedLanguages: Language[] = [];

  languageFormData: { [key: string]: any } = {}; // Stores form data for each language
  previousLanguage: number = 2;

  public store360Fields = [
    { label: 'Entrance Center', type: 'image', id: 2 },
    { label: 'Entrance Left', type: 'image', id: 2 },
    { label: 'Entrance Right', type: 'image', id: 2 },
    { label: 'HA Common Display', type: 'image', id: 2 }
  ];

  // Add these field type options
  public displayPlanogramFieldTypes = [
    { value: 1, label: 'Text' },
    { value: 5, label: 'Number' },
    { value: 7, label: 'Increment Decrement' },
    { value: 12, label: 'Attachment' },
    { value: 14, label: 'Date' }
  ];

  public marketActivitiesFieldTypes = [
    { value: 2, label: 'Image' },
  ];

  today = new Date();

  // Add this property to store Display Planogram default fields
  public displayPlanogramFields = [
    { label: 'DYNAMIC_FORM.DISPLAY_PLANOGRAM.BRAND', type: 'Text Field', id: 7 },
    { label: 'DYNAMIC_FORM.DISPLAY_PLANOGRAM.COMMON_DISPLAY', type: 'Text Field', id: 7 },
    { label: 'DYNAMIC_FORM.DISPLAY_PLANOGRAM.END_CAP', type: 'Text Field', id: 7 },
    { label: 'DYNAMIC_FORM.DISPLAY_PLANOGRAM.ISLAND', type: 'Text Field', id: 7 },
    { label: 'DYNAMIC_FORM.DISPLAY_PLANOGRAM.BRAND_BOX', type: 'Text Field', id: 7 },
    { label: 'DYNAMIC_FORM.DISPLAY_PLANOGRAM.BRAND_SHOP', type: 'Text Field', id: 7 },
    { label: 'DYNAMIC_FORM.DISPLAY_PLANOGRAM.OTHER_BRANDING', type: 'Text Field', id: 7 },
    { label: 'DYNAMIC_FORM.DISPLAY_PLANOGRAM.UPLOAD_IMAGE', type: 'Text Field', id: 12 }
  ];

  // Update the displayShareFields to use translation keys
  private displayShareFields: DisplayShareField[] = [
    { label: 'DYNAMIC_FORM.DISPLAY_SHARE.SEGMENT', id: 7, order: 1 },
    { label: 'DYNAMIC_FORM.DISPLAY_SHARE.ELECTROLUX', id: 7, order: 2 },
    { label: 'DYNAMIC_FORM.DISPLAY_SHARE.SAMSUNG', id: 7, order: 3 },
    { label: 'DYNAMIC_FORM.DISPLAY_SHARE.LG', id: 7, order: 4 },
    { label: 'DYNAMIC_FORM.DISPLAY_SHARE.BOSH', id: 7, order: 5 },
    { label: 'DYNAMIC_FORM.DISPLAY_SHARE.SIEMENS', id: 7, order: 6 }
  ];

  // Add these field type options for POSM fields
  public posmFieldTypes = [
    { value: 1, label: 'Text' },
    { value: 8, label: 'Yes/No' },
    { value: 2, label: 'Image' },
  ];

  public shopProfilingFieldTypes = [
    { value: 1, label: 'Text' },
    { value: 7, label: 'Increment Decrement' },
  ];

  // Update the posmFields property without the model field
  public posmFields = [
    // Remove the model field from here
    { label: 'Dose this product have POSM?', type: 'Yes/No', id: 8 },
    { label: 'Is it displayed as per guideline', type: 'Yes/No', id: 8 },
    { label: 'Take the picture of product (before)', type: 'Image', id: 2 },
    { label: 'Take the picture of product (after)', type: 'Image', id: 2 },
    { label: 'Picture Comment', type: 'Text', id: 1 }
  ];

  // Add this to your component class properties section
  promoterShareFields: TableField[] = [
    { label: 'DYNAMIC_FORM.PROMOTER_SHARE.MDA_GENERAL', type: 'Numbers', id: 7 }, // Using increment-decrement (7)
    { label: 'DYNAMIC_FORM.PROMOTER_SHARE.SDA', type: 'Numbers', id: 7 }, // Using increment-decrement (7)
    { label: 'DYNAMIC_FORM.PROMOTER_SHARE.BUILT_IN', type: 'Numbers', id: 7 }, // Using increment-decrement (7)
  ];

  // Add this property to your component class
  public salesDateValidations = {
    selectCurrentPastOne: true,
    selectCurrentPast: false,
    selectCurrentFutureOne: false,
    selectCurrentFuture: false,
    selectCurrent: true
  };

  // Add Shop Profiling fields
  private shopProfilingFields = [
    { label: 'Brand', id: 1 },
    { label: 'Quantity', id: 7 },
    { label: 'Value', id: 1 }
  ];

  // Add this property to your component class
  public marketActivitiesFields = [
    { id: 2, label: 'Brand', order: 1 },
    { id: 2, label: 'Market Activities', order: 2 },
    { id: 2, label: 'Upload Image', order: 3 },
  ];

  constructor(
    private fb: FormBuilder,
    private scrollDispatcher: ScrollDispatcher,
    private translateService: TranslateService,
    private toastr: ToastrService,
    private surveyService: SurveyServiceService,
    private router: Router
  ) {
    this.selectedQuestionType = this.defaultQuestionType;

    // Initialize form control with English (ID: 2) as default
    this.languageControl = this.fb.control(2, { nonNullable: true });

    // Initialize with English
    this.translateService.setDefaultLang('en');
    this.translateService.use('en');

    // Update subscription to handle language changes
    this.languageControl.valueChanges.subscribe((value: number) => {
      this.handleLanguageChange(value);
    });
  }

  public ngOnInit() {
    this.clearQuestions();
    if (this.initialSurveyData?.length) {
      this.initializeFromData(this.initialSurveyData);
    }

    if (this.allowedQuestionTypes) {
      this.questionTypes = this.questionTypes.filter(type =>
        this.allowedQuestionTypes?.includes(type.value)
      );
    }

    // Always set English (ID: 2) as default language
    this.selectedLanguage = 2;
    this.languageControl.setValue(2);

    // Update surveyData with default language
    this.surveyData = {
      ...this.surveyData,
      languageId: 2
    };
  }

  private initializeFromData(data: any[]) {
    data.forEach((question, index) => {
      // Check if this is API response format (has fieldType object instead of fieldTypeId)
      const fieldTypeId = question.fieldType ? question.fieldType.id : question.fieldTypeId;

      if (fieldTypeId === 11) { // Store360 template from API (uses ID 11)
        const store360Form = this.fb.group({
          id: [question.id || 0],
          label: ['Store360'],
          fieldTypeId: [6], // Use 6 for our internal form representation
          required: [question.required || false],
          order: [index + 1],
          note: [question.hint || ''], // Use hint from API response
          children: this.fb.array(
            (question.children || []).map((child: any) => {
              // Handle API response format where fieldType is an object
              const childFieldTypeId = child.fieldType ? child.fieldType.id : child.fieldTypeId;

              return this.fb.group({
                id: [child.id || 0],
                label: [child.label || ''],
                fieldTypeId: [childFieldTypeId || 2], // Default to image type (2)
                required: [child.required || false],
                order: [child.order || 1],
                note: [child.hint || ''], // Use hint from API response
                options: [[]],
                validation: this.fb.group({
                  allowGallery: [child.validation?.allowGallery || false],
                  allowMultiple: [child.validation?.allowMultiple || false],
                  singleImage: [child.validation?.singleImage || false],
                  askCategory: [child.validation?.askCategory || false],
                  askComment: [child.validation?.askComment || false],
                  embedTag: [child.validation?.embedTag || false],
                  accumulativeGraph: [child.validation?.accumulativeGraph || false],
                  allowScoring: [child.validation?.allowScoring || false],
                  showPreviousInfo: [child.validation?.showPreviousInfo || false]
                }),
                children: this.fb.array(
                  // Map the grandchildren (Upload Image and Description)
                  (child.children || []).map((grandchild: any) => {
                    const grandchildFieldTypeId = grandchild.fieldType ? grandchild.fieldType.id : grandchild.fieldTypeId;

                    return this.fb.group({
                      id: [grandchild.id || 0],
                      label: [grandchild.label || ''],
                      fieldTypeId: [grandchildFieldTypeId || 2],
                      required: [grandchild.required || false],
                      order: [grandchild.order || 1],
                      options: [[]],
                      validation: this.fb.group({
                        allowGallery: [grandchild.validation?.allowGallery || false],
                        allowMultiple: [grandchild.validation?.allowMultiple || false],
                        singleImage: [grandchild.validation?.singleImage || false],
                        askCategory: [grandchild.validation?.askCategory || false],
                        askComment: [grandchild.validation?.askComment || false],
                        embedTag: [grandchild.validation?.embedTag || false],
                        accumulativeGraph: [grandchild.validation?.accumulativeGraph || false],
                        allowScoring: [grandchild.validation?.allowScoring || false],
                        showPreviousInfo: [grandchild.validation?.showPreviousInfo || false]
                      })
                    });
                  })
                )
              });
            })
          )
        });

        this.setupQuestionFormSubscriptions(store360Form);
        this.questions.push(store360Form);
      } else if (fieldTypeId === 18) { // Promoter Share template
        const promoterShareForm = this.fb.group({
          id: [question.id || 0],
          label: [question.label || 'Promoter Share'],
          fieldTypeId: [18],
          required: [question.required || false],
          order: [index + 1],
          children: this.fb.array(
            // Skip the first child (Brand) as it's handled separately in the UI
            // and only map the remaining children
            question.children
              .filter((child: any) => child.fieldType?.id !== 19) // Filter out the Brand field (ID 19)
              .map((child: any) => {
                // Handle API response format where fieldType is an object
                const childFieldTypeId = child.fieldType ? child.fieldType.id : child.fieldTypeId;

                return this.fb.group({
                  id: [child.id || 0],
                  label: [child.label || ''],
                  fieldTypeId: [childFieldTypeId || 7], // Default to increment-decrement (7)
                  required: [child.required || false],
                  order: [child.order || 1],
                  validation: this.fb.group({
                    allowGallery: [child.validation?.allowGallery || false],
                    allowMultiple: [child.validation?.allowMultiple || false],
                    singleImage: [child.validation?.singleImage || false],
                    askCategory: [child.validation?.askCategory || false],
                    askComment: [child.validation?.askComment || false],
                    embedTag: [child.validation?.embedTag || false],
                    accumulativeGraph: [child.validation?.accumulativeGraph || false],
                    allowScoring: [child.validation?.allowScoring || false],
                    showPreviousInfo: [child.validation?.showPreviousInfo || false]
                  })
                });
              })
          )
        });

        this.setupQuestionFormSubscriptions(promoterShareForm);
        this.questions.push(promoterShareForm);
      } else if (fieldTypeId === 6 || fieldTypeId === 7) { // Sellout template (API uses 6, our form uses 7)
        const questionForm = this.fb.group({
          id: [question.id || 0],
          label: [question.label || 'Sellout'],
          fieldTypeId: [7], // Always use 7 for our internal form representation
          required: [question.required || false],
          order: [index + 1],
          children: this.fb.array(
            (question.children || []).map((child: any) => {
              // Handle API response format where fieldType is an object
              const childFieldTypeId = child.fieldType ? child.fieldType.id : child.fieldTypeId;

              return this.fb.group({
                id: [child.id || 0],
                label: [child.label || ''],
                fieldTypeId: [childFieldTypeId || 1],
                required: [child.required || false],
                order: [child.order || 0],
                validation: this.fb.group({
                  allowGallery: [child.validation?.allowGallery || false],
                  allowMultiple: [child.validation?.allowMultiple || false],
                  singleImage: [child.validation?.singleImage || false],
                  askCategory: [child.validation?.askCategory || false],
                  askComment: [child.validation?.askComment || false],
                  embedTag: [child.validation?.embedTag || false],
                  accumulativeGraph: [child.validation?.accumulativeGraph || false],
                  allowScoring: [child.validation?.allowScoring || false],
                  showPreviousInfo: [child.validation?.showPreviousInfo || false],
                  // Date field validations
                  selectCurrentPastOne: [child.validation?.selectCurrentPastOne || false],
                  selectCurrentPast: [child.validation?.selectCurrentPast || false],
                  selectCurrentFutureOne: [child.validation?.selectCurrentFutureOne || false],
                  selectCurrentFuture: [child.validation?.selectCurrentFuture || false],
                  selectCurrent: [child.validation?.selectCurrent || false]
                })
              });
            })
          )
        });

        this.setupQuestionFormSubscriptions(questionForm);
        this.questions.push(questionForm);

        // If this is a Sales Date field, update the salesDateValidations
        const salesDateField = question.children?.find((child: any) =>
          child.label === 'Sales Date' ||
          child.label === this.translateService.instant('DYNAMIC_FORM.SELLOUT.SALES_DATE'));

        if (salesDateField && salesDateField.validation) {
          this.salesDateValidations = {
            selectCurrentPastOne: salesDateField.validation.selectCurrentPastOne || false,
            selectCurrentPast: salesDateField.validation.selectCurrentPast || false,
            selectCurrentFutureOne: salesDateField.validation.selectCurrentFutureOne || false,
            selectCurrentFuture: salesDateField.validation.selectCurrentFuture || false,
            selectCurrent: salesDateField.validation.selectCurrent || false
          };
        }
      } else if (fieldTypeId === 6) { // Store360 template
        const store360Form = this.fb.group({
          id: [question.id || 0],
          label: ['Store360'],
          fieldTypeId: [6],
          required: [question.required || false],
          order: [index + 1],
          note: [question.note || ''], // Add note at the parent level
          children: this.fb.array(
            (question.children || this.getDefaultStore360Cards()).map((child: any) =>
              this.fb.group({
                id: [child.id || 0],
                label: [child.label || '', Validators.required],
                fieldTypeId: [child.fieldTypeId || 2],
                required: [child.required || false],
                order: [child.order || 1],
                note: [child.note || ''], // Add note for each child
                options: [[]],
                validation: this.fb.group({
                  allowGallery: [child.validation?.allowGallery || false],
                  allowMultiple: [child.validation?.allowMultiple || false],
                  singleImage: [child.validation?.singleImage || false],
                  askCategory: [child.validation?.askCategory || false],
                  askComment: [child.validation?.askComment || false],
                  embedTag: [child.validation?.embedTag || false],
                  accumulativeGraph: [child.validation?.accumulativeGraph || false],
                  allowScoring: [child.validation?.allowScoring || false],
                  showPreviousInfo: [child.validation?.showPreviousInfo || false]
                }),
                children: this.fb.array([
                  // Upload Image
                  this.fb.group({
                    id: [0],
                    label: 'Upload Image',
                    fieldTypeId: [3],
                    required: [false],
                    order: [1],
                    options: [[]],
                    validation: this.fb.group({
                      allowGallery: [false],
                      allowMultiple: [false],
                      singleImage: [false],
                      askCategory: [false],
                      askComment: [false],
                      embedTag: [false],
                      accumulativeGraph: [false],
                      allowScoring: [false],
                      showPreviousInfo: [false]
                    })
                  }),
                  // Description
                  this.fb.group({
                    id: [0],
                    label: 'Description',
                    fieldTypeId: [3],
                    required: [false],
                    order: [2],
                    options: [[]],
                    validation: this.fb.group({
                      allowGallery: [false],
                      allowMultiple: [false],
                      singleImage: [false],
                      askCategory: [false],
                      askComment: [false],
                      embedTag: [false],
                      accumulativeGraph: [false],
                      allowScoring: [false],
                      showPreviousInfo: [false]
                    })
                  })
                ])
              })
            )
          )
        });

        this.setupQuestionFormSubscriptions(store360Form);
        this.questions.push(store360Form);
      } else if (fieldTypeId === 15) { // Display Planogram template
        const displayPlanogramForm = this.fb.group({
          id: [question.id || 0],
          label: [question.label || 'Display Planogram'],
          fieldTypeId: [15],
          required: [question.required || false],
          order: [index + 1],
          children: this.fb.array(
            (question.children || this.displayPlanogramFields).map((child: any) =>
              this.fb.group({
                id: [child.id || 0],
                label: [child.label || '', Validators.required],
                fieldTypeId: [child.fieldTypeId || child.id || 1],
                required: [child.required || false],
                order: [child.order || 1]
              })
            )
          )
        });

        this.setupQuestionFormSubscriptions(displayPlanogramForm);
        this.questions.push(displayPlanogramForm);
      } else if (fieldTypeId === 16) { // Display Share template
        const displayShareForm = this.fb.group({
          id: [question.id || 0],
          label: ['Display Share'],
          fieldTypeId: [16],
          required: [question.required || false],
          order: [index + 1],
          children: this.fb.array(
            this.displayShareFields.map(field =>
              this.fb.group({
                id: [0],
                label: [field.label],
                fieldTypeId: [field.id], // Using the id directly as fieldTypeId
                required: [false],
                order: [field.order]
              })
            )
          )
        });

        this.setupQuestionFormSubscriptions(displayShareForm);
        this.questions.push(displayShareForm);
      } else if (fieldTypeId === 17) { // POSM Deployment template
        const posmForm = this.fb.group({
          id: [question.id || 0],
          label: [question.label || 'POSM Deployment'],
          fieldTypeId: [17],
          required: [question.required || false],
          order: [index + 1],
          children: this.fb.array(
            (question.children || this.posmFields).map((child: any) => {
              // Skip the Model field as it's handled separately in the UI
              if (child.label === 'Model') return null;

              // Handle API response format where fieldType is an object
              const childFieldTypeId = child.fieldType ? child.fieldType.id : child.fieldTypeId;

              return this.fb.group({
                id: [child.id || 0],
                label: [child.label],
                fieldTypeId: [childFieldTypeId || child.id],
                required: [child.required || false],
                order: [child.order || 1],
                validation: this.fb.group({
                  allowGallery: [child.validation?.allowGallery || false],
                  allowMultiple: [child.validation?.allowMultiple || false],
                  singleImage: [child.validation?.singleImage || false],
                  askCategory: [child.validation?.askCategory || false],
                  askComment: [child.validation?.askComment || false],
                  embedTag: [child.validation?.embedTag || false],
                  accumulativeGraph: [child.validation?.accumulativeGraph || false],
                  allowScoring: [child.validation?.allowScoring || false],
                  showPreviousInfo: [child.validation?.showPreviousInfo || false]
                })
              });
            }).filter(Boolean) // Filter out null values (Model field)
          )
        });

        this.setupQuestionFormSubscriptions(posmForm);
        this.questions.push(posmForm);
      } else if (fieldTypeId === 20) { // Shop Profiling template
        const shopProfilingForm = this.fb.group({
          id: [0],
          label: ['Shop Profiling'],
          fieldTypeId: [20],
          required: [false],
          order: [this.questions.length + 1],
          children: this.fb.array(
            this.shopProfilingFields.map((field, index) =>
              this.fb.group({
                id: [0],
                label: [field.label],
                fieldTypeId: [field.id],
                required: [false],
                order: [index + 1]
              })
            )
          )
        });

        this.questions.push(shopProfilingForm);
        this.updateSurveyData();
        this.selectedQuestionIndex = this.questions.length - 1;

        // Scroll to new question if not first question
        if (this.questions.length > 1) {
          setTimeout(() => {
            const questionCards = document.querySelectorAll('.question-card');
            const newCard = questionCards[questionCards.length - 1];
            if (newCard) {
              newCard.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
            }
          }, 100);
        }
      } else if (fieldTypeId === 21) { // Market Activities template
        const marketActivitiesForm = this.fb.group({
          id: [0],
          label: ['Market Activities'],
          fieldTypeId: [21],
          required: [false],
          order: [this.questions.length + 1],
          children: this.fb.array(
            this.marketActivitiesFields.map((field, index) =>
              this.fb.group({
                id: [0],
                label: [field.label],
                fieldTypeId: [field.id],
                required: [false],
                order: [index + 1]
              })
            )
          )
        });

        this.questions.push(marketActivitiesForm);
        this.updateSurveyData();
        this.selectedQuestionIndex = this.questions.length - 1;

        // Scroll to new question if not first question
        if (this.questions.length > 1) {
          setTimeout(() => {
            const questionCards = document.querySelectorAll('.question-card');
            const newCard = questionCards[questionCards.length - 1];
            if (newCard) {
              newCard.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
            }
          }, 100);
        }
      } else {
        // Handle other question types as before
        const questionForm = this.fb.group({
          id: [question.id || 0],
          label: [question.label || ''],
          fieldTypeId: [fieldTypeId || 1],
          required: [question.required || false],
          order: [index + 1],
          options: this.fb.array(
            (question.options || []).map((opt: any, optIndex: number) =>
              this.fb.group({
                id: [opt.id || 0],
                label: [opt.label || opt.text || ''],
                value: [opt.value || opt.label || opt.text || ''],
                order: [opt.order || optIndex + 1]
              })
            )
          ),
          validation: this.fb.group({
            allowGallery: [question.validation?.allowGallery || false],
            allowMultiple: [question.validation?.allowMultiple || false],
            singleImage: [question.validation?.singleImage || false],
            askCategory: [question.validation?.askCategory || false],
            askComment: [question.validation?.askComment || false],
            embedTag: [question.validation?.embedTag || false],
            accumulativeGraph: [question.validation?.accumulativeGraph || false],
            allowScoring: [question.validation?.allowScoring || false],
            showPreviousInfo: [question.validation?.showPreviousInfo || false],
          })
        });

        this.setupQuestionFormSubscriptions(questionForm);
        this.questions.push(questionForm);
      }
    });

    this.updateSurveyData();
  }

  private setupQuestionFormSubscriptions(questionForm: FormGroup) {
    questionForm.get('fieldTypeId')?.valueChanges.subscribe((newType: number | null) => {
      const options = questionForm.get('options') as FormArray;
      options.clear();

      // Convert numeric code back to type string for switch case
      const typeValue = this.questionTypes.find(t => t.code === newType)?.value || 'radio';

      if (typeValue === 'radio' || typeValue === 'multipleChoice') {
        options.push(this.fb.group({
          id: [0],
          label: ['Option 1'],
          value: ['Option 1'],
          order: [1]
        }));
        options.push(this.fb.group({
          id: [0],
          label: ['Option 2'],
          value: ['Option 2'],
          order: [2]
        }));
      } else if (typeValue === 'yesNo') {
        options.push(this.fb.group({
          id: [0],
          label: ['Yes'],
          value: ['Yes'],
          order: [1]
        }));
        options.push(this.fb.group({
          id: [0],
          label: ['No'],
          value: ['No'],
          order: [2]
        }));
      }
    });

    // Add subscription for children array changes
    const children = questionForm.get('children') as FormArray;
    if (children) {
      children.controls.forEach(childControl => {
        childControl.valueChanges.subscribe(() => {
          this.updateSurveyData();
        });
      });
    }

    questionForm.valueChanges.subscribe(() => {
      this.updateSurveyData();
    });
  }

  private updateSurveyData() {
    const formData = this.questions.map((question, index) => {
      const fieldTypeId = question.get('fieldTypeId')?.value;

      // Handle Promoter Share template
      if (fieldTypeId === 18) {
        return {
          id: question.get('id')?.value || 0,
          label: 'Promoter Share',
          fieldTypeId: 18,
          required: question.get('required')?.value || false,
          order: index + 1,
          children: [
            // Add static Brand field with ID 19 directly in the output JSON
            {
              id: 0,
              label: 'Brand',
              fieldTypeId: 19,
              required: false,
              order: 1,
              options: [],
              validation: {
                allowGallery: false,
                allowMultiple: false,
                singleImage: false,
                askCategory: false,
                askComment: false,
                embedTag: false,
                accumulativeGraph: false,
                allowScoring: false,
                showPreviousInfo: false
              }
            },
            // Then include all the regular children from the form
            ...(question.get('children') as FormArray).controls.map((child, childIndex) => {
              const childLabel = child.get('label')?.value;
              // Translate the label if it's a default field
              const translatedLabel = this.isPromoterShareDefaultField(childLabel)
                ? this.translateService.instant(childLabel)
                : childLabel;

              return {
                id: child.get('id')?.value || 0,
                label: translatedLabel,
                fieldTypeId: child.get('fieldTypeId')?.value,
                required: child.get('required')?.value || false,
                order: childIndex + 2, // Increment order by 2 since we added Brand as first item
                options: [] as Array<{ id: number; label: string; value: string; order: number }>,
                validation: child.get('validation')?.value || {
                  allowGallery: false,
                  allowMultiple: false,
                  singleImage: false,
                  askCategory: false,
                  askComment: false,
                  embedTag: false,
                  accumulativeGraph: false,
                  allowScoring: false,
                  showPreviousInfo: false
                }
              };
            })
          ]
        };
      }

      // Handle Store360 template
      if (fieldTypeId === 6) {
        return {
          id: question.get('id')?.value || 0,
          label: 'store360',
          fieldTypeId: 11,
          order: index + 1,
          children: (question.get('children') as FormArray).controls.map(child => ({
            id: child.get('id')?.value || 0,
            label: child.get('label')?.value,
            fieldTypeId: child.get('fieldTypeId')?.value,
            required: child.get('required')?.value || false,
            order: child.get('order')?.value,
            hint: child.get('note')?.value || '', // Changed from note to hint
            options: [],
            validation: child.get('validation')?.value || {
              allowGallery: false,
              allowMultiple: false,
              singleImage: false,
              askCategory: false,
              askComment: false,
              embedTag: false,
              accumulativeGraph: false,
              allowScoring: false,
              showPreviousInfo: false
            },
            children: [
              {
                id: 0,
                label: 'Upload Image',
                fieldTypeId: 2,
                required: false,
                order: 1,
                options: [],
                validation: {
                  allowGallery: true,
                  allowMultiple: true,
                  singleImage: false,
                  askCategory: false,
                  askComment: true,
                  embedTag: false,
                  accumulativeGraph: false,
                  allowScoring: false,
                  showPreviousInfo: false
                }
              },
              {
                id: 0,
                label: 'Description',
                fieldTypeId: 13, // Changed from 3 to 13
                required: false,
                order: 2,
                options: [],
                validation: {
                  allowGallery: false,
                  allowMultiple: false,
                  singleImage: false,
                  askCategory: false,
                  askComment: false,
                  embedTag: false,
                  accumulativeGraph: false,
                  allowScoring: false,
                  showPreviousInfo: false
                }
              }
            ]
          }))
        };
      }

      // Handle Sellout template
      if (fieldTypeId === 7) {
        return {
          id: question.get('id')?.value || 0,
          label: question.get('label')?.value,
          fieldTypeId: 6,
          required: question.get('required')?.value || false,
          order: index + 1,
          children: (question.get('children') as FormArray).controls.map(child => {
            const childLabel = child.get('label')?.value;
            // Translate the label if it's a default field
            const translatedLabel = this.isSelloutDefaultField(childLabel)
              ? this.translateService.instant(childLabel)
              : childLabel;

            // Check if this is the Sales Date field
            const isSalesDateField = childLabel === 'DYNAMIC_FORM.SELLOUT.SALES_DATE';

            return {
              id: child.get('id')?.value || 0,
              label: translatedLabel,
              fieldTypeId: child.get('fieldTypeId')?.value,
              required: child.get('required')?.value || false,
              order: child.get('order')?.value,
              options: [] as Array<{ id: number; label: string; value: string; order: number }>,
              validation: isSalesDateField ? {
                // Add date validations for sales date field
                selectCurrentPastOne: this.salesDateValidations?.selectCurrentPastOne || false,
                selectCurrentPast: this.salesDateValidations?.selectCurrentPast || false,
                selectCurrentFutureOne: this.salesDateValidations?.selectCurrentFutureOne || false,
                selectCurrentFuture: this.salesDateValidations?.selectCurrentFuture || false,
                selectCurrent: this.salesDateValidations?.selectCurrent || false,
                allowGallery: false,
                allowMultiple: false,
                singleImage: false,
                askCategory: false,
                askComment: false,
                embedTag: false,
                accumulativeGraph: false,
                allowScoring: false,
                showPreviousInfo: false
              } : {
                // Default validations for other fields
                allowGallery: false,
                allowMultiple: false,
                singleImage: false,
                askCategory: false,
                askComment: false,
                embedTag: false,
                accumulativeGraph: false,
                allowScoring: false,
                showPreviousInfo: false
              }
            };
          })
        };
      }

      // Handle Display Planogram template - format similar to Sellout
      if (fieldTypeId === 15) {
        return {
          id: question.get('id')?.value || 0,
          label: question.get('label')?.value,
          fieldTypeId: 15,
          required: question.get('required')?.value || false,
          order: index + 1,
          children: (question.get('children') as FormArray).controls.map(child => {
            const childLabel = child.get('label')?.value;
            // Translate the label if it's a default field
            const translatedLabel = this.isDisplayPlanogramDefaultField(childLabel)
              ? this.translateService.instant(childLabel)
              : childLabel;

            return {
              id: child.get('id')?.value || 0,
              label: translatedLabel,
              fieldTypeId: child.get('fieldTypeId')?.value,
              required: child.get('required')?.value || false,
              order: child.get('order')?.value,
              options: [] as Array<{ id: number; label: string; value: string; order: number }>,
              validation: {
                allowGallery: false,
                allowMultiple: false,
                singleImage: false,
                askCategory: false,
                askComment: false,
                embedTag: false,
                accumulativeGraph: false,
                allowScoring: false,
                showPreviousInfo: false
              }
            };
          })
        };
      }

      // Handle Display Share template
      if (fieldTypeId === 16) {
        return {
          id: question.get('id')?.value || 0,
          label: 'Display Share',
          fieldTypeId: 16,
          required: question.get('required')?.value || false,
          order: index + 1,
          children: (question.get('children') as FormArray).controls.map(child => ({
            id: child.get('id')?.value || 0,
            // Translate the label if it's a translation key
            label: child.get('label')?.value.startsWith('DYNAMIC_FORM.DISPLAY_SHARE.') ?
              this.translateService.instant(child.get('label')?.value) :
              child.get('label')?.value,
            fieldTypeId: child.get('fieldTypeId')?.value,
            required: child.get('required')?.value || false,
            order: child.get('order')?.value,
            options: [],
            validation: {
              allowGallery: false,
              allowMultiple: false,
              singleImage: false,
              askCategory: false,
              askComment: false,
              embedTag: false,
              accumulativeGraph: false,
              allowScoring: false,
              showPreviousInfo: false
            }
          }))
        };
      }

      // Handle POSM Deployment template - format similar to Sellout
      if (fieldTypeId === 17) {
        return {
          id: question.get('id')?.value || 0,
          label: question.get('label')?.value,
          fieldTypeId: 17,
          required: question.get('required')?.value || false,
          order: index + 1,
          children: [
            // Add static Model field with ID 19 directly in the output JSON
            {
              id: 0,
              label: 'Model',
              fieldTypeId: 19,
              required: false,
              order: 1,
              options: [],
              validation: {
                allowGallery: false,
                allowMultiple: false,
                singleImage: false,
                askCategory: false,
                askComment: false,
                embedTag: false,
                accumulativeGraph: false,
                allowScoring: false,
                showPreviousInfo: false
              }
            },
            // Then include all the regular children from the form
            ...(question.get('children') as FormArray).controls.map(child => {
              const childLabel = child.get('label')?.value;
              // Translate the label if it's a default field
              const translatedLabel = this.isPosmDefaultField(childLabel)
                ? this.translateService.instant(childLabel)
                : childLabel;

              return {
                id: child.get('id')?.value || 0,
                label: translatedLabel,
                fieldTypeId: child.get('fieldTypeId')?.value,
                required: child.get('required')?.value || false,
                order: child.get('order')?.value + 1, // Increment order by 1 since we added Model as first item
                options: [] as Array<{ id: number; label: string; value: string; order: number }>,
                validation: child.get('validation')?.value || {
                  allowGallery: false,
                  allowMultiple: false,
                  singleImage: false,
                  askCategory: false,
                  askComment: false,
                  embedTag: false,
                  accumulativeGraph: false,
                  allowScoring: false,
                  showPreviousInfo: false
                }
              };
            })
          ]
        };
      }

      // Handle Shop Profiling template
      if (fieldTypeId === 20) {
        return {
          id: question.get('id')?.value || 0,
          label: question.get('label')?.value || 'Shop Profiling',
          fieldTypeId: 20,
          required: question.get('required')?.value || false,
          order: index + 1,
          children: (question.get('children') as FormArray).controls.map(child => ({
            id: child.get('id')?.value || 0,
            label: child.get('label')?.value,
            fieldTypeId: child.get('fieldTypeId')?.value,
            required: child.get('required')?.value || false,
            order: child.get('order')?.value || 1,
            options: [],
            validation: child.get('validation')?.value || {
              allowGallery: false,
              allowMultiple: false,
              singleImage: false,
              askCategory: false,
              askComment: false,
              embedTag: false,
              accumulativeGraph: false,
              allowScoring: false,
              showPreviousInfo: false
            }
          }))
        };
      }

      // Handle Market Activities template
      if (fieldTypeId === 21) {
        return {
          id: question.get('id')?.value || 0,
          label: 'Market Activities',
          fieldTypeId: 21,
          required: question.get('required')?.value || false,
          order: index + 1,
          children: (question.get('children') as FormArray).controls.map(child => ({
            id: child.get('id')?.value || 0,
            label: child.get('label')?.value,
            fieldTypeId: child.get('fieldTypeId')?.value,
            required: child.get('required')?.value || false,
            order: child.get('order')?.value || 1,
            options: [],
            validation: child.get('validation')?.value || {
              allowGallery: false,
              allowMultiple: false,
              singleImage: false,
              askCategory: false,
              askComment: false,
              embedTag: false,
              accumulativeGraph: false,
              allowScoring: false,
              showPreviousInfo: false
            }
          }))
        };
      }

      // Handle other question types
      const baseQuestion = {
        id: question.get('id')?.value || 0,
        label: question.get('label')?.value || '',
        fieldTypeId: fieldTypeId,
        required: question.get('required')?.value || false,
        order: index + 1,
        options: [] as Array<{ id: number; label: string; value: string; order: number }>,
        validation: question.get('validation')?.value || {}
      };

      // Handle options for radio, multipleChoice, etc.
      if (question.contains('options')) {
        const options = question.get('options') as FormArray;
        baseQuestion.options = options.controls.map(option => ({
          id: option.get('id')?.value || 0,
          label: option.get('label')?.value,
          value: option.get('value')?.value,
          order: option.get('order')?.value
        }));
      }

      return baseQuestion;
    });

    // Update survey data
    this.surveyData = {
      ...this.surveyData,
      questions: formData
    };
    this.surveyDataChange.emit(this.surveyData);
  }

  public selectQuestionType(type: string) {
    this.selectedQuestionType = type;
    if (type === 'date') {
      const questionForm = this.createQuestionForm(12);
      this.questions.push(questionForm);
      this.updateSurveyData();
      this.selectedQuestionIndex = this.questions.length - 1;
    } else {
      this.addQuestion();
    }
  }

  public addQuestion() {
    if (this.readOnly) return;

    // Find the selected question type object
    const selectedType = this.questionTypes.find(type => type.value === this.selectedQuestionType);
    const typeCode = selectedType?.code || 1;

    const questionForm = this.createQuestionForm(typeCode);

    // Add initial options if needed
    if ([4, 8, 9].includes(typeCode)) {  // Radio, YesNo, MultipleChoice
      const optionsArray = questionForm.get('options') as FormArray;
      optionsArray.push(this.createOptionForm());
      optionsArray.push(this.createOptionForm());
    }

    questionForm.valueChanges.subscribe(() => {
      this.updateSurveyData();
    });

    this.questions.push(questionForm);
    this.updateSurveyData();
    this.selectedQuestionIndex = this.questions.length - 1;

    // Only apply focus and scroll behavior if it's not the first question
    if (this.questions.length > 1) {
      setTimeout(() => {
        const questionCards = document.querySelectorAll('.question-card');
        const newCard = questionCards[questionCards.length - 1];

        if (newCard) {
          // Scroll the new card into view
          newCard.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });

          // Find and focus the label element
          const labelElement = newCard.querySelector('.question-label') as HTMLElement;
          if (labelElement) {
            // Trigger edit mode
            this.startEditingQuestion(this.questions[this.questions.length - 1], new MouseEvent('click'));

            // Focus the input after a short delay to ensure edit mode is active
            setTimeout(() => {
              const input = newCard.querySelector('input[formcontrolname="label"]') as HTMLInputElement;
              if (input) {
                input.focus();
                input.select();
              }
            }, 100);
          }
        }
      }, 100);
    }

    this.questionAdded.emit(this.questions[this.questions.length - 1].value);
  }

  private createQuestionForm(typeCode: number) {
    const form = this.fb.group({
      id: [0],
      label: ['', Validators.required],
      fieldTypeId: [typeCode],
      required: [false],
      order: [this.questions.length + 1],
      options: this.fb.array([]),
      validation: this.fb.group({
        allowGallery: [false],
        allowMultiple: [false],
        singleImage: [false],
        askCategory: [false],
        askComment: [false],
        embedTag: [false],
        accumulativeGraph: [false],
        allowScoring: [false],
        showPreviousInfo: [false],
      })
    });

    // For date type, ensure the label is properly initialized
    if (typeCode === 12) {
      form.patchValue({
        fieldTypeId: 12,
        label: '' // Initialize empty label that will be filled by user
      });
    }

    return form;
  }

  private createOptionForm() {
    return this.fb.group({
      id: [0],
      label: ['', Validators.required],
      value: [''],
      order: [0]
    });
  }

  public addOption(questionForm: FormGroup) {
    const options = questionForm.get('options') as FormArray;
    options.push(this.createOptionForm());

    // Update the current language data to include the new option
    if (this.languageData[this.selectedLanguage]) {
      const questionIndex = this.questions.indexOf(questionForm);
      if (questionIndex !== -1) {
        const currentQuestionData = this.languageData[this.selectedLanguage][questionIndex];
        if (currentQuestionData && currentQuestionData.options) {
          currentQuestionData.options.push({
            id: 0,
            label: '',
            value: '',
            order: options.length
          });
        }
      }
    }

    this.updateSurveyData();
  }

  public removeOption(questionForm: FormGroup, index: number) {
    const options = questionForm.get('options') as FormArray;
    if (options.length > 2) {
      options.removeAt(index);

      // Update options order
      options.controls.forEach((control, idx) => {
        control.patchValue({ order: idx + 1 });
      });

      // Update the current language data to remove the option
      if (this.languageData[this.selectedLanguage]) {
        const questionIndex = this.questions.indexOf(questionForm);
        if (questionIndex !== -1) {
          const currentQuestionData = this.languageData[this.selectedLanguage][questionIndex];
          if (currentQuestionData && currentQuestionData.options) {
            currentQuestionData.options.splice(index, 1);
            // Update remaining options order
            currentQuestionData.options.forEach((opt: any, idx: number) => {
              opt.order = idx + 1;
            });
          }
        }
      }

      this.updateSurveyData();
    }
  }

  public getOptionsFormArray(questionForm: FormGroup): FormArray {
    return questionForm.get('options') as FormArray;
  }

  public deleteQuestion(index: number, event?: MouseEvent) {
    if (this.readOnly) return;

    // Stop event propagation
    event?.stopPropagation();

    // Update selected question index
    if (this.selectedQuestionIndex === index) {
      this.selectedQuestionIndex = null;
    } else if (this.selectedQuestionIndex !== null && this.selectedQuestionIndex > index) {
      this.selectedQuestionIndex--;
    }

    this.questions.splice(index, 1);

    // Update sequence numbers for remaining questions
    this.questions.forEach((question, idx) => {
      question.patchValue({ order: idx + 1 });
    });

    this.updateSurveyData();
    this.questionDeleted.emit(index);
  }

  public isEditingQuestion(questionForm: FormGroup): boolean {
    const key = `question_${this.questions.indexOf(questionForm)}`;
    return this.questionEditingStates[key] || false;
  }

  public startEditingQuestion(questionForm: FormGroup, event: MouseEvent): void {
    const key = `question_${this.questions.indexOf(questionForm)}`;

    Object.keys(this.questionEditingStates).forEach((k) => {
      this.questionEditingStates[k] = false;
    });

    this.questionEditingStates[key] = true;

    requestAnimationFrame(() => {
      const input = document.querySelector('input[formcontrolname="label"]') as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
    });

    document.addEventListener('click', this.handleQuestionClickOutside);
  }

  public stopEditingQuestion(questionForm: FormGroup): void {
    const key = `question_${this.questions.indexOf(questionForm)}`;
    this.questionEditingStates[key] = false;
    document.removeEventListener('click', this.handleQuestionClickOutside);
  }

  private handleQuestionClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!target.closest('.question-input') && !target.closest('.question-label')) {
      Object.keys(this.questionEditingStates).forEach((key) => {
        if (this.questionEditingStates[key]) {
          const questionIndex = parseInt(key.split('_')[1]);
          const questionForm = this.questions[questionIndex];
          this.stopEditingQuestion(questionForm);
        }
      });
    }
  };

  public onOptionDrop(questionForm: FormGroup, event: CdkDragDrop<string[]>) {
    const options = questionForm.get('options') as FormArray;
    moveItemInArray(options.controls, event.previousIndex, event.currentIndex);

    const values = options.controls.map(control => ({
      label: control.value.label,
      value: control.value.label
    }));

    while (options.length) {
      options.removeAt(0);
    }

    values.forEach((value, index) => {
      options.push(
        this.fb.group({
          id: [0],
          label: [value.label],
          value: [value.value],
          order: [index + 1]
        })
      );
    });

    questionForm.markAsDirty();
    this.updateSurveyData();
  }

  public onMandatoryAnswerChange(event: any) {
    if (this.selectedQuestionIndex !== null) {
      const questionForm = this.questions[this.selectedQuestionIndex];
      questionForm.patchValue({ required: event.checked });
      this.updateSurveyData();
    }
  }

  public selectQuestion(index: number, event?: MouseEvent) {
    if (this.readOnly) return;

    // Stop event propagation if event exists
    if (event) {
      event.stopPropagation();
    }

    this.selectedQuestionIndex = index;
    this.questionSelected.emit(index);
  }

  public isQuestionSelected(index: number): boolean {
    return this.selectedQuestionIndex === index;
  }

  public ngOnDestroy() {
    document.removeEventListener('click', this.handleQuestionClickOutside);
  }

  public onQuestionDrop(event: CdkDragDrop<string[]>) {
    if (this.selectedQuestionIndex === event.previousIndex) {
      this.selectedQuestionIndex = event.currentIndex;
    }
    else if (this.selectedQuestionIndex !== null &&
      event.previousIndex > this.selectedQuestionIndex &&
      event.currentIndex <= this.selectedQuestionIndex) {
      this.selectedQuestionIndex++;
    }
    else if (this.selectedQuestionIndex !== null &&
      event.previousIndex < this.selectedQuestionIndex &&
      event.currentIndex >= this.selectedQuestionIndex) {
      this.selectedQuestionIndex--;
    }

    moveItemInArray(this.questions, event.previousIndex, event.currentIndex);
    this.questions.forEach((question, index) => {
      question.patchValue({ order: index + 1 });
    });
    this.updateSurveyData();
  }

  public cancelForm() {
    console.log('Form cancelled');
  }

  public saveForm() {
    // Mark all fields as touched to trigger validation styling
    this.questions.forEach(questionForm => {
      const fieldTypeId = questionForm.get('fieldTypeId')?.value;

      // Skip label validation for Store360 template
      if (fieldTypeId !== 6) {
        questionForm.get('label')?.markAsTouched();
      }

      const options = questionForm.get('options') as FormArray;
      options?.controls.forEach(option => {
        option.get('label')?.markAsTouched();
      });

      // Add validation for sellout and display planogram table fields
      if (fieldTypeId === 7 || fieldTypeId === 15) { // Sellout or Display Planogram template
        const children = questionForm.get('children') as FormArray;
        children.controls.forEach(child => {
          child.get('label')?.markAsTouched();
        });
      }

      // Add validation for POSM fields
      if (fieldTypeId === 17) { // POSM Deployment template
        const children = questionForm.get('children') as FormArray;
        children.controls.forEach(child => {
          child.get('label')?.markAsTouched();
        });
      }
    });

    // Check for empty required fields
    let hasErrors = false;

    this.questions.forEach((questionForm, questionIndex) => {
      const fieldTypeId = questionForm.get('fieldTypeId')?.value;

      // Skip label validation for Store360 template
      if (fieldTypeId !== 6 && !questionForm.get('label')?.value) {
        this.toastr.error(`Question ${questionIndex + 1} is required`);
        hasErrors = true;
      }

      if (fieldTypeId === 7) { // Sellout template
        const children = questionForm.get('children') as FormArray;
        children.controls.forEach((child, childIndex) => {
          if (!child.get('label')?.value) {
            this.toastr.error(`Field ${childIndex + 1} in Sellout table is required`);
            hasErrors = true;
          }
        });
      } else if (fieldTypeId === 15) { // Display Planogram template
        const children = questionForm.get('children') as FormArray;
        children.controls.forEach((child, childIndex) => {
          if (!child.get('label')?.value) {
            this.toastr.error(`Field ${childIndex + 1} in Display Planogram is required`);
            hasErrors = true;
          }
        });
      } else if (fieldTypeId === 17) { // POSM Deployment template
        const children = questionForm.get('children') as FormArray;
        children.controls.forEach((child, childIndex) => {
          if (!child.get('label')?.value) {
            this.toastr.error(`Field ${childIndex + 1} in POSM Deployment is required`);
            hasErrors = true;
          }
        });
      } else if ([4, 8, 9].includes(fieldTypeId)) {  // Radio, YesNo, MultipleChoice
        const options = questionForm.get('options') as FormArray;
        options.controls.forEach((option, optionIndex) => {
          if (!option.get('label')?.value) {
            this.toastr.error(`Option ${optionIndex + 1} in Question ${questionIndex + 1} is required`);
            hasErrors = true;
          }
        });
      }

      // Add Store360 validation - only validate child labels
      if (fieldTypeId === 6) { // Store360 type
        const children = questionForm.get('children') as FormArray;
        children.controls.forEach((child, childIndex) => {
          if (!child.get('label')?.value) {
            this.toastr.error(`Field ${childIndex + 1} in Store360 is required`);
            hasErrors = true;
          }
        });
      }
    });

    if (hasErrors) {
      return;
    }

    // Ensure current language data is saved with translations
    this.languageFormData[this.selectedLanguage] = this.questions.map(question => {
      const value = question.value;
      if (value.fieldTypeId === 7) {
        return {
          ...value,
          children: value.children.map((child: any) => {
            const isDefaultField = this.isSelloutDefaultField(child.label);
            return {
              ...child,
              label: isDefaultField ?
                this.translateService.instant(child.label) :
                child.label
            };
          })
        };
      } else if (value.fieldTypeId === 15) {
        return {
          ...value,
          children: value.children.map((child: any) => {
            const isDefaultField = this.isDisplayPlanogramDefaultField(child.label);
            return {
              ...child,
              label: isDefaultField ?
                this.translateService.instant(child.label) :
                child.label
            };
          })
        };
      } else if (value.fieldTypeId === 16) { // Display Share template
        return {
          ...value,
          children: value.children.map((child: any) => {
            const isDefaultField = child.label.startsWith('DYNAMIC_FORM.DISPLAY_SHARE.');
            return {
              ...child,
              label: isDefaultField ?
                this.translateService.instant(child.label) :
                child.label
            };
          })
        };
      }
      return value;
    });

    // Add the language to savedLanguages if not already present
    if (!this.savedLanguages.find(lang => lang.id === this.selectedLanguage)) {
      const language = this.availableLanguages.find(lang => lang.id === this.selectedLanguage);
      if (language) {
        this.savedLanguages.push(language);
      }
    }

    // If no validation errors, emit the form data
    this.formSaved.emit(this.surveyData as SurveyData);
  }

  public onConditionChange(condition: string, checked: boolean) {
    if (this.selectedQuestionIndex !== null) {
      const questionForm = this.questions[this.selectedQuestionIndex];
      const validationGroup = questionForm.get('validation') as FormGroup;
      validationGroup.patchValue({ [condition]: checked });
      this.updateSurveyData();
    }
  }

  public duplicateQuestion(index: number, event?: MouseEvent) {
    if (this.readOnly) return;

    const questionForm = this.questions[index];
    const fieldTypeId = questionForm.get('fieldTypeId')?.value;

    // Prevent duplication for Sellout, Store360, Display Planogram, Display Share, POSM, and Promoter Share templates
    if (fieldTypeId === 7 || fieldTypeId === 6 || fieldTypeId === 15 ||
      fieldTypeId === 16 || fieldTypeId === 17 || fieldTypeId === 18) {
      return;
    }

    // Stop event propagation
    event?.stopPropagation();

    // Deep clone the form group
    const originalForm = this.questions[index];
    const clonedValue = JSON.parse(JSON.stringify(originalForm.value));

    // Find existing copies of this question to determine the next copy number
    const baseText = clonedValue.label.replace(/ \(Copy \d+\)$/, '').replace(/ \(Copy\)$/, '');
    const copyRegex = new RegExp(`^${baseText} \\(Copy( \\d+)?\\)$`);

    let maxCopyNumber = 0;
    this.questions.forEach(question => {
      const questionText = question.get('label')?.value;
      const match = questionText.match(copyRegex);
      if (match) {
        const copyNumber = match[1] ? parseInt(match[1].trim()) : 1;
        maxCopyNumber = Math.max(maxCopyNumber, copyNumber);
      }
    });

    const copyNumber = maxCopyNumber > 0 ? ` (Copy ${maxCopyNumber + 1})` : ' (Copy)';

    // Create a new form group with the cloned values
    const duplicatedForm = this.fb.group({
      id: [0],
      label: [`${baseText}${copyNumber}`],
      fieldTypeId: [clonedValue.fieldTypeId],
      required: [clonedValue.required],
      order: [this.questions.length + 1],
      options: this.fb.array(
        clonedValue.options.map((opt: any, optIndex: number) =>
          this.fb.group({
            id: [0],
            label: [opt.label],
            value: [opt.value],
            order: [optIndex + 1]
          })
        )
      ),
      validation: this.fb.group({
        allowGallery: [clonedValue.validation?.allowGallery || false],
        allowMultiple: [clonedValue.validation?.allowMultiple || false],
        singleImage: [clonedValue.validation?.singleImage || false],
        askCategory: [clonedValue.validation?.askCategory || false],
        askComment: [clonedValue.validation?.askComment || false],
        embedTag: [clonedValue.validation?.embedTag || false],
        accumulativeGraph: [clonedValue.validation?.accumulativeGraph || false],
        allowScoring: [clonedValue.validation?.allowScoring || false],
        showPreviousInfo: [clonedValue.validation?.showPreviousInfo || false],
      })
    });

    // Set up the same subscriptions as for new questions
    this.setupQuestionFormSubscriptions(duplicatedForm);

    // Insert the duplicated question after the current one
    this.questions.splice(index + 1, 0, duplicatedForm);

    // Update the sequence numbers
    this.questions.forEach((question, idx) => {
      question.patchValue({ order: idx + 1 });
    });

    // Select the new question
    this.selectQuestion(index + 1);

    // Update the survey data
    this.updateSurveyData();

    // Scroll to the new question
    requestAnimationFrame(() => {
      const questionCards = document.querySelectorAll('.question-card');
      const newCard = questionCards[index + 1];
      if (newCard) {
        newCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  public updateYesNoOption(questionForm: FormGroup, index: number, newValue: string) {
    const options = questionForm.get('options') as FormArray;
    const optionGroup = options.at(index) as FormGroup;

    optionGroup.patchValue({
      label: newValue,
      value: newValue,
      order: index + 1
    });

    // This will trigger the form valueChanges and update the JSON
    this.updateSurveyData();
  }

  public toggleTemplateMenu() {
    this.isTemplateMenuOpen = !this.isTemplateMenuOpen;
  }

  public selectTemplateItem(item: TemplateItem) {
    // Check if template already exists
    const templateExists = this.questions.some(question => {
      const fieldTypeId = question.get('fieldTypeId')?.value;
      // Check for Sellout (7), Store360 (6), Display Planogram (15), Display Share (16), POSM (17), Promoter Share (18), and Market Activities (21) templates
      return (fieldTypeId === item.id ||
        (item.id === 13 && fieldTypeId === 6) ||
        (item.id === 14 && fieldTypeId === 15) ||
        (item.id === 16 && fieldTypeId === 16) ||
        (item.id === 17 && fieldTypeId === 17) ||
        (item.id === 18 && fieldTypeId === 18) ||
        (item.id === 21 && fieldTypeId === 21));
    });

    if (templateExists) {
      // Show a toastr message indicating template already exists
      this.toastr.warning('This template has already been added');
      return;
    }

    this.selectedTemplateItem = item.id;
    if (item.id === 7) { // Sellout template
      const selloutForm = this.fb.group({
        id: [0],
        label: [item.name],
        fieldTypeId: [item.id],
        required: [false],
        order: [this.questions.length + 1],
        children: this.fb.array(
          // Initialize with all default sellout fields
          this.selloutTable.map((field, index) =>
            this.fb.group({
              id: [0],
              label: [field.label],
              fieldTypeId: [field.id], // Use the predefined field type IDs
              required: [true],
              order: [index + 1]
            })
          )
        )
      });

      this.questions.push(selloutForm);
      this.updateSurveyData();
      this.selectedQuestionIndex = this.questions.length - 1;

      // Only scroll if it's not the first question
      if (this.questions.length > 1) {
        setTimeout(() => {
          const questionCards = document.querySelectorAll('.question-card');
          const newCard = questionCards[questionCards.length - 1];

          if (newCard) {
            newCard.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }, 100);
      }
    } else if (item.id === 13) { // Store360 template
      const store360Form = this.fb.group({
        id: [0],
        label: ['Store360'],
        fieldTypeId: [6], // Store360 type
        required: [false],
        order: [this.questions.length + 1],
        note: [''], // Add note at the parent level
        children: this.fb.array(
          this.store360Fields.map((field, index) =>
            this.fb.group({
              id: [0],
              label: [field.label],
              fieldTypeId: [field.id],
              required: [false],
              order: [index + 1],
              note: [''], // Add note for each child
              options: [[]],
              validation: this.fb.group({
                allowGallery: [false],
                allowMultiple: [false],
                singleImage: [false],
                askCategory: [false],
                askComment: [false],
                embedTag: [false],
                accumulativeGraph: [false],
                allowScoring: [false],
                showPreviousInfo: [false]
              })
            })
          )
        )
      });

      // Set up subscriptions for the Store360 form
      this.setupQuestionFormSubscriptions(store360Form);

      // Set up subscriptions for each child control
      const children = store360Form.get('children') as FormArray;
      children.controls.forEach(childControl => {
        childControl.valueChanges.subscribe(() => {
          this.updateSurveyData();
        });
      });

      this.questions.push(store360Form);
      this.updateSurveyData();
      this.selectedQuestionIndex = this.questions.length - 1;

      // Scroll to new question if not first question
      if (this.questions.length > 1) {
        setTimeout(() => {
          const questionCards = document.querySelectorAll('.question-card');
          const newCard = questionCards[questionCards.length - 1];

          if (newCard) {
            newCard.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }, 100);
      }
    } else if (item.id === 14) { // Display Planogram template
      const displayPlanogramForm = this.fb.group({
        id: [0],
        label: ['Display Planogram'],
        fieldTypeId: [15],
        required: [false],
        order: [this.questions.length + 1],
        children: this.fb.array(
          // Use the displayPlanogramFields array instead of hardcoding
          this.displayPlanogramFields.map((field, index) =>
            this.fb.group({
              id: [0],
              label: [field.label],
              fieldTypeId: [field.id],
              required: [false],
              order: [index + 1]
            })
          )
        )
      });

      this.questions.push(displayPlanogramForm);
      this.updateSurveyData();
      this.selectedQuestionIndex = this.questions.length - 1;

      // Scroll to new question if not first question
      if (this.questions.length > 1) {
        setTimeout(() => {
          const questionCards = document.querySelectorAll('.question-card');
          const newCard = questionCards[questionCards.length - 1];
          if (newCard) {
            newCard.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }, 100);
      }
    } else if (item.id === 16) { // Display Share template
      const displayShareForm = this.fb.group({
        id: [0],
        label: ['Display Share'],
        fieldTypeId: [16],
        required: [false],
        order: [this.questions.length + 1],
        children: this.fb.array(
          this.displayShareFields.map(field =>
            this.fb.group({
              id: [0],
              label: [field.label],
              fieldTypeId: [field.id],
              required: [false],
              order: [field.order]
            })
          )
        )
      });

      this.questions.push(displayShareForm);
      this.updateSurveyData();
      this.selectedQuestionIndex = this.questions.length - 1;

      // Scroll to new question if not first question
      if (this.questions.length > 1) {
        setTimeout(() => {
          const questionCards = document.querySelectorAll('.question-card');
          const newCard = questionCards[questionCards.length - 1];
          if (newCard) {
            newCard.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }, 100);
      }
    } else if (item.id === 17) { // POSM Deployment template
      const posmForm = this.fb.group({
        id: [0],
        label: ['POSM Deployment'],
        fieldTypeId: [17],
        required: [false],
        order: [this.questions.length + 1],
        children: this.fb.array(
          this.posmFields.map((field, index) =>
            this.fb.group({
              id: [0],
              label: [field.label],
              fieldTypeId: [field.id],
              required: [false],
              order: [index + 1]
            })
          )
        )
      });

      this.questions.push(posmForm);
      this.updateSurveyData();
      this.selectedQuestionIndex = this.questions.length - 1;

      // Scroll to new question if not first question
      if (this.questions.length > 1) {
        setTimeout(() => {
          const questionCards = document.querySelectorAll('.question-card');
          const newCard = questionCards[questionCards.length - 1];
          if (newCard) {
            newCard.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }, 100);
      }
    } else if (item.id === 18) { // Promoter Share template
      const promoterShareForm = this.fb.group({
        id: [0],
        label: ['Promoter Share'],
        fieldTypeId: [18],
        required: [false],
        order: [this.questions.length + 1],
        children: this.fb.array(
          this.promoterShareFields.map((field, index) =>
            this.fb.group({
              id: [0],
              label: [field.label],
              fieldTypeId: [field.id],
              required: [false],
              order: [index + 1]
            })
          )
        )
      });

      this.questions.push(promoterShareForm);
      this.updateSurveyData();
      this.selectedQuestionIndex = this.questions.length - 1;

      // Scroll to new question if not first question
      if (this.questions.length > 1) {
        setTimeout(() => {
          const questionCards = document.querySelectorAll('.question-card');
          const newCard = questionCards[questionCards.length - 1];
          if (newCard) {
            newCard.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }, 100);
      }
    } else if (item.id === 20) { // Shop Profiling template
      const shopProfilingForm = this.fb.group({
        id: [0],
        label: ['Shop Profiling'],
        fieldTypeId: [20],
        required: [false],
        order: [this.questions.length + 1],
        children: this.fb.array(
          this.shopProfilingFields.map((field, index) =>
            this.fb.group({
              id: [0],
              label: [field.label],
              fieldTypeId: [field.id],
              required: [false],
              order: [index + 1]
            })
          )
        )
      });

      this.questions.push(shopProfilingForm);
      this.updateSurveyData();
      this.selectedQuestionIndex = this.questions.length - 1;

      // Scroll to new question if not first question
      if (this.questions.length > 1) {
        setTimeout(() => {
          const questionCards = document.querySelectorAll('.question-card');
          const newCard = questionCards[questionCards.length - 1];
          if (newCard) {
            newCard.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }, 100);
      }
    } else if (item.id === 21) { // Market Activities template
      const marketActivitiesForm = this.fb.group({
        id: [0],
        label: ['Market Activities'],
        fieldTypeId: [21],
        required: [false],
        order: [this.questions.length + 1],
        children: this.fb.array(
          this.marketActivitiesFields.map((field, index) =>
            this.fb.group({
              id: [0],
              label: [field.label],
              fieldTypeId: [field.id],
              required: [false],
              order: [index + 1]
            })
          )
        )
      });

      this.questions.push(marketActivitiesForm);
      this.updateSurveyData();
      this.selectedQuestionIndex = this.questions.length - 1;

      // Scroll to new question if not first question
      if (this.questions.length > 1) {
        setTimeout(() => {
          const questionCards = document.querySelectorAll('.question-card');
          const newCard = questionCards[questionCards.length - 1];
          if (newCard) {
            newCard.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }, 100);
      }
    }
  }

  public getChildrenFormArray(questionForm: FormGroup): FormArray {
    return questionForm.get('children') as FormArray;
  }

  public addTableField(questionForm: FormGroup) {
    const children = questionForm.get('children') as FormArray;
    const newField = this.fb.group({
      id: [0],
      label: ['', Validators.required],
      fieldTypeId: [5], // Default to Number type
      required: [true],
      order: [children.length + 1]
    });

    // Get the translated placeholder text
    const translatedLabel = this.translateService.instant('DYNAMIC_FORM.SELLOUT.FIELD_LABEL');
    newField.patchValue({ label: translatedLabel });

    children.push(newField);
    this.updateSurveyData();
  }

  public removeTableField(questionForm: FormGroup, field: AbstractControl) {
    const children = questionForm.get('children') as FormArray;
    const index = children.controls.indexOf(field);
    const fieldTypeId = questionForm.get('fieldTypeId')?.value;

    if (index > -1) {
      children.removeAt(index);

      // Update order of remaining fields
      children.controls.forEach((control, idx) => {
        control.patchValue({ order: idx + 1 });
      });

      this.updateSurveyData();
    }
  }

  // Add this method to check if a field can be removed (for both Sellout and Display Planogram)
  public canRemoveField(questionForm: FormGroup, field: AbstractControl): boolean {
    const childrenArray = this.getChildrenFormArray(questionForm);
    // Allow removal if there's more than one field
    return childrenArray.length > 1;
  }

  public removeLanguage(language: Language, event: MouseEvent) {
    event.stopPropagation();
    this.savedLanguages = this.savedLanguages.filter(lang => lang.id !== language.id);
    // Emit the language ID when removed
    this.languageRemoved.emit(language.id);
  }

  private handleLanguageChange(newLanguageId: number) {
    // Save current language data before switching
    if (this.selectedLanguage) {
      this.languageData[this.selectedLanguage] = this.questions.map(form => ({
        ...form.value,
        children: form.get('fieldTypeId')?.value === 7 ?
          form.get('children')?.value.map((child: any) => ({
            ...child,
            label: this.isSelloutDefaultField(child.label) ?
              child.label : child.label
          })) :
          form.get('children')?.value
      }));
    }

    this.selectedLanguage = newLanguageId;
    const selectedLang = this.availableLanguages.find(lang => lang.id === newLanguageId);
    if (selectedLang) {
      this.translateService.use(selectedLang.code);

      // Wait for translations to be loaded
      setTimeout(() => {
        this.questions.forEach((questionForm, index) => {
          const savedData = this.languageData[newLanguageId]?.[index];
          const fieldTypeId = questionForm.get('fieldTypeId')?.value;

          if (fieldTypeId === 7) { // Sellout template
            const children = questionForm.get('children') as FormArray;
            children.controls.forEach(child => {
              const currentLabel = child.get('label')?.value;
              if (this.isSelloutDefaultField(currentLabel)) {
                // Update with new language translation
                child.patchValue({
                  label: child.get('label')?.value
                });
              } else if (savedData) {
                // Use saved data for custom fields
                const savedChild = savedData.children.find((c: any) => c.order === child.get('order')?.value);
                if (savedChild) {
                  child.patchValue({
                    label: savedChild.label
                  });
                }
              }
            });
          } else if (fieldTypeId === 16) { // Display Share template
            const children = questionForm.get('children') as FormArray;
            children.controls.forEach(child => {
              const currentLabel = child.get('label')?.value;
              if (this.isDisplayShareDefaultField(currentLabel)) {
                // Update with new language translation
                child.patchValue({
                  label: currentLabel // Keep the translation key
                });
              } else if (savedData) {
                // Use saved data for custom fields
                const savedChild = savedData.children.find((c: any) => c.order === child.get('order')?.value);
                if (savedChild) {
                  child.patchValue({
                    label: savedChild.label
                  });
                }
              }
            });
          }
        });

        this.updateSurveyData();
      }, 100);
    }
  }

  private updateQuestionFormValues(form: FormGroup, data: any) {
    // Special handling for sellout template
    if (form.get('fieldTypeId')?.value === 7) {
      form.patchValue({
        label: 'Sellout',
        required: data.required
      });

      const childrenArray = form.get('children') as FormArray;
      const defaultSelloutFields = this.selloutTable.map(field => field.label);

      data.children.forEach((child: any, index: number) => {
        if (childrenArray.at(index)) {
          const currentLabel = childrenArray.at(index).get('label')?.value;
          if (defaultSelloutFields.includes(currentLabel)) {
            // For default fields, use translation
            childrenArray.at(index).patchValue({
              label: child.label
            });
          } else {
            // For custom fields, use saved data
            childrenArray.at(index).patchValue({
              label: child.label
            });
          }
        }
      });
      return;
    }

    // Handle other question types as before
    if (data.options && form.contains('options')) {
      const optionsArray = form.get('options') as FormArray;
      const currentLength = optionsArray.length;
      const newDataLength = data.options.length;

      if (newDataLength > currentLength) {
        for (let i = currentLength; i < newDataLength; i++) {
          optionsArray.push(this.fb.group({
            id: [0],
            label: [data.options[i].label],
            value: [data.options[i].value],
            order: [i + 1]
          }));
        }
      }

      data.options.forEach((option: any, index: number) => {
        if (optionsArray.at(index)) {
          optionsArray.at(index).patchValue({
            label: option.label,
            value: option.value
          });
        }
      });
    }

    // Update children if they exist (for sellout template)
    if (data.children && form.contains('children')) {
      const childrenArray = form.get('children') as FormArray;
      data.children.forEach((child: any, index: number) => {
        if (childrenArray.at(index)) {
          childrenArray.at(index).patchValue({
            label: child.label
          });
        }
      });
    }
  }

  private resetQuestionFormValues(form: FormGroup) {
    // Special handling for sellout template
    if (form.get('fieldTypeId')?.value === 7) {
      // Keep the original "Sellout" label
      form.patchValue({
        label: 'Sellout'
      });

      const childrenArray = form.get('children') as FormArray;
      const defaultSelloutFields = this.selloutTable.map(field => field.label);

      childrenArray.controls.forEach((control, index) => {
        // Only reset label if it's not one of the default sellout fields
        const currentLabel = control.get('label')?.value;
        if (!defaultSelloutFields.includes(currentLabel)) {
          control.patchValue({
            label: ''
          });
        }
      });
      return;
    }

    // Handle other question types as before
    if (form.contains('options')) {
      const optionsArray = form.get('options') as FormArray;
      const englishData = this.languageData[2];
      const englishQuestion = englishData?.find((q: any) => q.id === form.get('id')?.value);

      optionsArray.controls.forEach((control, index) => {
        control.patchValue({
          label: '',
          value: '',
          order: index + 1
        });
      });

      if (englishQuestion && englishQuestion.options) {
        const currentLength = optionsArray.length;
        const englishLength = englishQuestion.options.length;

        for (let i = currentLength; i < englishLength; i++) {
          optionsArray.push(this.fb.group({
            id: [0],
            label: [''],
            value: [''],
            order: [i + 1]
          }));
        }
      }
    }
  }

  public isSelloutDefaultField(label: string): boolean {
    return this.selloutTable.some(field => field.label === label);
  }

  // Add new property to track if all languages are saved
  public get areAllLanguagesSaved(): boolean {
    // If there are no available languages, return false
    if (!this.availableLanguages.length) return false;

    // Check if all available languages are in savedLanguages
    return this.availableLanguages.every(availableLang =>
      this.savedLanguages.some(savedLang => savedLang.id === availableLang.id)
    );
  }

  onLanguageChange(event: any) {
    // Save current form data for the previous language
    if (this.questions.length) {
      this.languageFormData[this.previousLanguage] = this.questions.map(question => ({
        ...question.value,
        options: question.get('options')?.value || [],
        children: question.get('children')?.value || []
      }));
    }

    // Clear label values for the new language
    this.questions.forEach(question => {
      // Clear main question label
      if (question.get('fieldTypeId')?.value !== 7) { // Skip sellout type
        question.patchValue({ label: '' });
      }

      // Clear options labels if they exist
      const options = question.get('options') as FormArray;
      if (options) {
        (options.controls as FormGroup[]).forEach(option => {
          option.patchValue({ label: '' });
        });
      }

      // Clear children labels if they exist (for table components)
      const children = question.get('children') as FormArray;
      if (children) {
        (children.controls as FormGroup[]).forEach(child => {
          if (!this.isSelloutDefaultField(child.get('label')?.value)) {
            child.patchValue({ label: '' });
          }
        });
      }
    });

    // Load saved data for the new language if it exists
    if (this.languageFormData[event.value]) {
      this.questions.forEach((question, index) => {
        const savedData = this.languageFormData[event.value][index];
        if (savedData) {
          question.patchValue({
            label: savedData.label,
            options: savedData.options,
            children: savedData.children
          });
        }
      });
    }

    this.previousLanguage = event.value;
  }

  public publishSurvey(): void {
    this.publishClicked.emit();
  }

  // Add a method to clear questions
  public clearQuestions(): void {
    this.questions = [];
    this.selectedQuestionIndex = null;
    this.questionEditingStates = {};
    // Emit empty array to notify parent
    this.surveyDataChange.emit({
      questions: [],
      languageId: this.selectedLanguage
    });
  }

  // Add helper method to check if it's a Store360 template
  public isStore360Template(fieldTypeId: number): boolean {
    return fieldTypeId === 6;
  }

  // Add this method to handle Store360 field deletion
  public removeStore360Field(questionForm: FormGroup, field: AbstractControl) {
    const children = questionForm.get('children') as FormArray;
    const index = children.controls.indexOf(field);

    if (index > -1) {
      children.removeAt(index);
      this.updateSurveyData();
    }
  }

  // Add this method to handle adding new Store360 fields
  public addStore360Field(questionForm: FormGroup) {
    const children = questionForm.get('children') as FormArray;
    const newField = this.fb.group({
      id: [0],
      label: ['', Validators.required],
      fieldTypeId: [2],
      required: [false],
      order: [children.length + 1],
      note: [''], // Add note field
      options: [[]],
      validation: this.fb.group({
        allowGallery: [false],
        allowMultiple: [false],
        singleImage: [false],
        askCategory: [false],
        askComment: [false],
        embedTag: [false],
        accumulativeGraph: [false],
        allowScoring: [false],
        showPreviousInfo: [false]
      }),
      children: this.fb.array([
        // Upload Image
        this.fb.group({
          id: [0],
          label: 'Upload Image',
          fieldTypeId: [3],
          required: [false],
          order: [1],
          options: [[]],
          validation: this.fb.group({
            allowGallery: [false],
            allowMultiple: [false],
            singleImage: [false],
            askCategory: [false],
            askComment: [false],
            embedTag: [false],
            accumulativeGraph: [false],
            allowScoring: [false],
            showPreviousInfo: [false]
          })
        }),
        // Description
        this.fb.group({
          id: [0],
          label: 'Description',
          fieldTypeId: [3],
          required: [false],
          order: [2],
          options: [[]],
          validation: this.fb.group({
            allowGallery: [false],
            allowMultiple: [false],
            singleImage: [false],
            askCategory: [false],
            askComment: [false],
            embedTag: [false],
            accumulativeGraph: [false],
            allowScoring: [false],
            showPreviousInfo: [false]
          })
        })
      ])
    });

    children.push(newField);
    this.updateSurveyData();
  }

  // Add new method to handle mandatory checkbox changes
  public onStore360MandatoryChange(event: any, questionForm: FormGroup, field: AbstractControl) {
    field.patchValue({
      required: event.checked,
      children: field.get('children')?.value?.map((child: any) => ({
        ...child,
        required: event.checked
      }))
    });
    this.updateSurveyData();
  }

  // Add helper method to get default Store360 cards
  private getDefaultStore360Cards(): any[] {
    return [
      {
        label: 'Entrance Center',
        fieldTypeId: 2,
        required: false,
        order: 1,
        children: this.getDefaultStore360Children()
      },
      {
        label: 'Entrance Left',
        fieldTypeId: 2,
        required: false,
        order: 2,
        children: this.getDefaultStore360Children()
      },
      {
        label: 'Entrance Right',
        fieldTypeId: 2,
        required: false,
        order: 3,
        children: this.getDefaultStore360Children()
      },
      {
        label: 'Entrance Back',
        fieldTypeId: 2,
        required: false,
        order: 4,
        children: this.getDefaultStore360Children()
      },
      {
        label: 'HA Common Display',
        fieldTypeId: 2,
        required: false,
        order: 5,
        children: this.getDefaultStore360Children()
      }
    ];
  }

  // Add a method to check if a label is a default Store360 label
  public isStore360DefaultLabel(label: string): boolean {
    const defaultLabels = [
      'Entrance Center',
      'Entrance Left',
      'Entrance Right',
      'Entrance Back',
      'HA Common Display'
    ];
    return defaultLabels.includes(label);
  }

  // Helper method to get default children for Store360 cards
  private getDefaultStore360Children(): any[] {
    return [
      {
        label: 'Upload Image',
        fieldTypeId: 3,
        required: false,
        order: 1
      },
      {
        label: 'Description',
        fieldTypeId: 13, // Changed from 3 to 13
        required: false,
        order: 2
      }
    ];
  }

  // Add a helper method to get translated label
  public getTranslatedLabel(label: string): string {
    return label.startsWith('DYNAMIC_FORM.STORE360.') ?
      this.translateService.instant(label) :
      label;
  }

  // Add helper method to check if it's a Display Planogram template
  public isDisplayPlanogramTemplate(fieldTypeId: number): boolean {
    return fieldTypeId === 15;
  }

  // Add method to add new field to Display Planogram
  public addDisplayPlanogramField(questionForm: FormGroup) {
    const children = questionForm.get('children') as FormArray;
    const newField = this.fb.group({
      id: [0],
      label: ['', Validators.required], // Empty label that user can fill
      fieldTypeId: [1], // Default to Text type
      required: [false],
      order: [children.length + 1]
    });

    children.push(newField);
    this.updateSurveyData();
  }

  public isSalesDateField(label: string): boolean {
    return label === 'DYNAMIC_FORM.SELLOUT.SALES_DATE';
  }

  // Add this method to check if a label is a default Display Planogram field
  public isDisplayPlanogramDefaultField(label: string): boolean {
    return this.displayPlanogramFields.some(field => field.label === label);
  }

  // Add helper method to check if it's a Display Share template
  public isDisplayShareTemplate(fieldTypeId: number): boolean {
    return fieldTypeId === 16;
  }

  // Add helper method to check if a field is a Display Share default field
  public isDisplayShareDefaultField(label: string): boolean {
    return label.startsWith('DYNAMIC_FORM.DISPLAY_SHARE.');
  }

  // Add method to add new display share field
  public addDisplayShareField(questionForm: FormGroup) {
    const childrenArray = this.getChildrenFormArray(questionForm);
    const newField = this.fb.group({
      id: [0],
      label: [''],
      fieldTypeId: [4], // Default field type for Display Share
      required: [false],
      order: [childrenArray.length + 1],
      options: [], // Empty options array
      validation: this.fb.group({
        allowGallery: [false],
        allowMultiple: [false],
        singleImage: [false],
        askCategory: [false],
        askComment: [false],
        embedTag: [false],
        accumulativeGraph: [false],
        allowScoring: [false],
        showPreviousInfo: [false]
      })
    });
    childrenArray.push(newField);
    this.updateSurveyData();
  }

  // Add method to add new POSM field
  public addPosmField(questionForm: FormGroup) {
    const children = questionForm.get('children') as FormArray;

    children.push(this.fb.group({
      id: [0],
      label: ['New Field'],
      fieldTypeId: [1], // Default to Text
      required: [false],
      order: [children.length + 1],
      validation: this.fb.group({
        allowGallery: [false],
        allowMultiple: [false],
        singleImage: [false],
        askCategory: [false],
        askComment: [false],
        embedTag: [false],
        accumulativeGraph: [false],
        allowScoring: [false],
        showPreviousInfo: [false]
      })
    }));

    this.updateSurveyData();
  }

  // Add helper method to check if a field is a default POSM field
  public isPosmDefaultField(label: string): boolean {
    if (!label) return false;
    return this.posmFields.some(field => field.label === label);
  }

  // Add helper method to check if it's a Promoter Share template
  public isPromoterShareTemplate(fieldTypeId: number): boolean {
    return fieldTypeId === 18;
  }

  // Add helper method to check if a field is a Promoter Share default field
  public isPromoterShareDefaultField(label: string): boolean {
    return this.promoterShareFields.some(field => field.label === label);
  }

  // Add method to add new field to Promoter Share
  public addPromoterShareField(questionForm: FormGroup) {
    const children = questionForm.get('children') as FormArray;
    const newField = this.fb.group({
      id: [0],
      label: ['', Validators.required], // Empty label that user can fill
      fieldTypeId: [7], // Default to increment-decrement type
      required: [false],
      order: [children.length + 1]
    });

    children.push(newField);
    this.updateSurveyData();
  }

  // Add this method to handle sales date condition changes
  public onSalesDateConditionChange(condition: string, checked: boolean): void {
    if (this.selectedQuestionIndex !== null) {
      this.salesDateValidations = {
        ...this.salesDateValidations,
        [condition]: checked
      };

      // Update the form data to reflect the changes
      this.updateSurveyData();
    }
  }

  // Add helper method to check if it's a Shop Profiling template
  public isShopProfilingTemplate(fieldTypeId: number): boolean {
    return fieldTypeId === 20;
  }

  // Add helper method to check if a field is a Shop Profiling default field
  public isShopProfilingDefaultField(label: string): boolean {
    return this.shopProfilingFields.some(field => field.label === label);
  }

  // Add method to add new Shop Profiling field
  public addShopProfilingField(questionForm: FormGroup) {
    const children = questionForm.get('children') as FormArray;
    const newField = this.fb.group({
      id: [0],
      label: ['', Validators.required],
      fieldTypeId: [1], // Default to Text type
      required: [false],
      order: [children.length + 1]
    });

    children.push(newField);
    this.updateSurveyData();
  }

  /**
   * Loads existing questions from survey data
   * @param data The survey data containing questions to load
   */
  public loadExistingQuestions(data: any[]): void {
    // Clear existing questions first
    this.clearQuestions();

    // Initialize from the provided data
    if (data && data.length > 0) {
      this.initializeFromData(data);
    }
  }

  // Add this method to check if a field is a default Market Activities field
  public isMarketActivitiesDefaultField(label: string): boolean {
    return this.marketActivitiesFields.some(field => field.label === label);
  }

  // Add method to add new field to Market Activities
  public addMarketActivitiesField(questionForm: FormGroup) {
    const children = questionForm.get('children') as FormArray;
    const newField = this.fb.group({
      id: [0],
      label: ['', Validators.required], // Empty label that user can fill
      fieldTypeId: [1], // Default to Text type
      required: [false],
      order: [children.length + 1]
    });

    children.push(newField);
    this.updateSurveyData();
  }
}
